import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { FeatureFlag } from '@core/enums/feature-flag';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { ReportLayoutChart } from 'app/modules/reporting/reporting.types';
import { guid } from 'app/shared/Utils/common.utils';
import { ChartType } from 'app/shared/components/chart/chart.types';
import { noop, Observable, tap } from 'rxjs';

@Component({
  selector: 'app-reporting-builder-visualizations-chart-setup',
  templateUrl: './chart-setup.component.html',
  styleUrls: ['./chart-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartSetupComponent extends OnDestroyMixin implements OnInit {
  @Input() fields: FieldDef[];
  @Input() set title(value: string) {
    this.form?.get('title').setValue(value);
  }
  @Input() visualization: Partial<ReportLayoutChart>;
  @Input() isTimeSeries: boolean;
  @Input() allowRelatedObjectFields = false;

  @Output() close = new EventEmitter();

  form = new FormGroup({
    title: new FormControl(''),
    chart_type: new FormControl('', [Validators.required]),
    summary_value: new FormControl(''),
    summary_type: new FormControl('', [Validators.required]),
    group_by: new FormControl('', [Validators.required]),
    series_value: new FormControl(''),
    display_fields: new FormControl([]),
    id: new FormControl(''),
  });

  get chartTypeControl(): AbstractControl {
    return this.form.get('chart_type');
  }

  get groupByControl(): AbstractControl {
    return this.form.get('group_by');
  }

  get seriesValueControl(): AbstractControl {
    return this.form.get('series_value');
  }

  get groupByValue(): string {
    return this.groupByControl.value;
  }

  get seriesValue(): string {
    return this.seriesValueControl.value;
  }

  get chartTypeValue(): string {
    return this.chartTypeControl.value;
  }

  get chartSummaryType(): AbstractControl {
    return this.form.get('summary_type');
  }

  get chartSummaryTypeValue(): string {
    return this.chartSummaryType.value;
  }

  get isChartTypeLine(): boolean {
    return this.chartTypeValue === ChartType.Line;
  }

  get isChartTypeDonut(): boolean {
    return this.chartTypeValue === ChartType.Donut;
  }

  get isChartTypeVerticalBar(): boolean {
    return this.chartTypeValue === ChartType.Bar;
  }

  get isChartTypeMetric(): boolean {
    return this.chartTypeValue === ChartType.Metric;
  }

  get isChartTypeTable(): boolean {
    return this.chartTypeValue === ChartType.Table;
  }

  get showSummaryField(): boolean {
    return ['average', 'sum'].includes(this.chartSummaryTypeValue);
  }

  get showXAxisAndSeries(): boolean {
    return ['horizontal-bar', 'bar', 'donut'].includes(this.chartTypeValue);
  }

  get hasTableVisualizationEnabled$(): Observable<boolean> {
    return this.featureFlagService.hasFeatureFlag(FeatureFlag.TABLE_REPORT_VISUALIZATION);
  }

  private lastChartType: string = null;

  constructor(private featureFlagService: FeatureFlagService) {
    super();
  }

  ngOnInit(): void {
    if (this.visualization) {
      this.form.setValue({
        id: guid(),
        title: this.visualization.title,
        chart_type: this.visualization.chart_type,
        group_by: this.visualization.group_by ?? '',
        series_value: this.visualization.series_value ?? '',
        summary_type: this.visualization.summary_type ?? '',
        summary_value: this.visualization.summary_value ?? '',
        display_fields: this.visualization.display_fields ?? [],
      });

      this.lastChartType = this.visualization.chart_type;

      setTimeout(() => {
        this.chartTypeControl.updateValueAndValidity();
        this.chartSummaryType.updateValueAndValidity();
        this.groupByControl.updateValueAndValidity();
      }, 0);
    }

    //[sc-20294] - add support for showing dates in bar charts
    if (this.isTimeSeries) {
      this.fields.push({ name: 'ts_date', label: 'Occurrence Date (TS)', type: 'text' });
    }

    this.chartTypeControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        tap((value) => {
          if (value !== this.lastChartType) {
            this.form.setValue(
              {
                id: guid(),
                title: this.visualization?.title ?? '',
                chart_type: value,
                group_by: '',
                series_value: '',
                summary_type: '',
                summary_value: '',
                display_fields: [],
              },
              { emitEvent: false },
            );
          }

          if (this.isChartTypeLine || this.isChartTypeMetric) {
            this.groupByControl.setValidators(null);
          } else if (this.isChartTypeTable) {
            this.groupByControl.setValidators(null);
            this.chartSummaryType.setValidators(null);
          } else {
            this.groupByControl.setValidators([Validators.required]);
            this.chartSummaryType.setValidators([Validators.required]);
          }
          this.groupByControl.updateValueAndValidity();
          this.chartSummaryType.updateValueAndValidity();
        }),
      )
      .subscribe(noop);
  }

  create(): void {
    const chart = this.form.getRawValue();

    if (chart.summary_type === 'total') {
      chart.summary_value = 'id';
    }

    if (!chart.id) {
      chart.id = guid();
    }

    this.close.emit(chart);
  }

  cancel(): void {
    this.close.emit(null);
  }

  filterGroupBy(source: FieldDef[], filter: string): FieldDef[] {
    if (filter) {
      return source.filter((x) => x.name !== filter);
    }

    return source;
  }

  filterSeriesValue(source: FieldDef[], filter: string): FieldDef[] {
    if (filter) {
      return source.filter((x) => x.name !== filter);
    }

    return source;
  }
}
